import React from 'react';

interface SyncStatusProps {
  syncStatus: {
    isBootstrapComplete: boolean;
    lastFullSync: string | null;
    lastIncrementalSync: string | null;
    totalContacts: number;
    personalBootstrapComplete: boolean;
    personalLastFullSync: string | null;
    personalLastIncrementalSync: string | null;
    personalTotalContacts: number;
    internalBootstrapComplete: boolean;
    internalLastFullSync: string | null;
    internalLastIncrementalSync: string | null;
    internalTotalUsers: number;
  };
  sharedUpn?: string;
  userEmail?: string;
  isSyncing: boolean;
  onClose?: () => void;
}

const SyncStatus: React.FC<SyncStatusProps> = ({
  syncStatus,
  sharedUpn,
  userEmail,
  isSyncing,
  onClose
}) => {
  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="absolute z-50 top-0 left-0 right-0 mx-auto max-w-sm bg-white border-2 border-blue-500 rounded-lg shadow-2xl p-4 mb-4">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Sync Status</h3>
        {onClose && (
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
            aria-label="Close sync status"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>
      
      <div className="space-y-4">
        {/* Corporate Contacts Status */}
        <div className="text-xs sm:text-sm text-gray-700 space-y-1">
          <h5 className="font-medium text-gray-900 text-sm sm:text-base">Corporate Contacts</h5>
          <div className="truncate">
            <strong>Shared Mailbox:</strong> {sharedUpn || 'Not set'}
          </div>
          <div>
            <strong>Bootstrap Complete:</strong> {syncStatus.isBootstrapComplete ? 'Yes' : 'No'}
          </div>
          <div>
            <strong>Total Contacts:</strong> {syncStatus.totalContacts.toLocaleString()}
          </div>
          <div className="truncate">
            <strong>Last Full Sync:</strong> {formatDate(syncStatus.lastFullSync)}
          </div>
          <div className="truncate">
            <strong>Last Incremental Sync:</strong> {formatDate(syncStatus.lastIncrementalSync)}
          </div>
        </div>

        {/* Personal Contacts Status */}
        <div className="text-xs sm:text-sm text-gray-700 space-y-1">
          <h5 className="font-medium text-gray-900 text-sm sm:text-base">Personal Contacts</h5>
          <div className="truncate">
            <strong>User:</strong> {userEmail || 'Not available'}
          </div>
          <div>
            <strong>Bootstrap Complete:</strong> {syncStatus.personalBootstrapComplete ? 'Yes' : 'No'}
          </div>
          <div>
            <strong>Total Contacts:</strong> {syncStatus.personalTotalContacts?.toLocaleString() || '0'}
          </div>
          <div className="truncate">
            <strong>Last Full Sync:</strong> {formatDate(syncStatus.personalLastFullSync)}
          </div>
          <div className="truncate">
            <strong>Last Incremental Sync:</strong> {formatDate(syncStatus.personalLastIncrementalSync)}
          </div>
        </div>

        {/* Internal Users Status */}
        <div className="text-xs sm:text-sm text-gray-700 space-y-1">
          <h5 className="font-medium text-gray-900 text-sm sm:text-base">Internal Users</h5>
          <div className="truncate">
            <strong>Organization:</strong> Members
          </div>
          <div>
            <strong>Bootstrap Complete:</strong> {syncStatus.internalBootstrapComplete ? 'Yes' : 'No'}
          </div>
          <div>
            <strong>Total Users:</strong> {syncStatus.internalTotalUsers?.toLocaleString() || '0'}
          </div>
          <div className="truncate">
            <strong>Last Full Sync:</strong> {formatDate(syncStatus.internalLastFullSync)}
          </div>
          <div className="truncate">
            <strong>Last Incremental Sync:</strong> {formatDate(syncStatus.internalLastIncrementalSync)}
          </div>
        </div>
      </div>

      <div className="mt-2 text-xs sm:text-sm text-gray-700">
        <div>
          <strong>Auto Sync:</strong> {isSyncing ? 'Running' : 'Enabled (15min intervals)'}
        </div>
      </div>
    </div>
  );
};

export default SyncStatus;

